import type {Nullish} from 'shared/web';

export type ReportDocumentValueKind = 'currency' | 'number' | 'percentage';

export interface ReportDocumentValueFormat {
  kind: ReportDocumentValueKind;
  maximumFractionDigits?: number;
  unit?: string;
}

export interface ReportDocumentFilter {
  label: string;
  value: string;
}

export interface ReportDocumentSummaryItem extends ReportDocumentValueFormat {
  percentage?: number | null;
  color: string;
  label: string;
  value: number | Nullish;
  secondaryValue?: {
    label: string;
    value: number | Nullish;
  };
}

export interface ReportDocumentTableColumn extends ReportDocumentValueFormat {
  color?: string;
  id: string;
  label: string;
  values: Array<number | null>;
  percentages?: Array<number | null>;
  /** Overrides the percentage in the exact-table footer without changing summary cards. */
  summaryPercentage?: number | null;
}

export interface ReportDocumentChartSeries extends ReportDocumentTableColumn {
  color: string;
  renderAs?: 'bar' | 'line';
  strokeDasharray?: string;
}

export interface ReportDocumentSection {
  categoryType?: 'month' | 'label';
  chartType?: 'bar' | 'line';
  categories: string[];
  description?: string;
  id: string;
  incompleteCategories?: string[];
  isStacked?: boolean;
  // Legacy joined text renders first; suffix deduplication applies only to legacy notes.
  note?: string;
  // Use fragments throughout a group for shared warnings; cross-mode matching is exact.
  notes?: string[];
  orientation?: 'horizontal' | 'vertical';
  series: ReportDocumentChartSeries[];
  showZeroLine?: boolean;
  summaryItems?: ReportDocumentSummaryItem[];
  tableColumns?: ReportDocumentTableColumn[];
  title: string;
  targetRange?: {min: number; max: number; color: string};
}

export interface ReportDocumentSectionGroup {
  sectionIds: string[];
  combineTables?: boolean;
}

export interface ChartReportDocument {
  context: string;
  currency: string;
  filters: ReportDocumentFilter[];
  footer: string;
  generatedAt: string;
  generatedBy: string;
  includeContext: boolean;
  locale: string;
  metadata: string[];
  sections: ReportDocumentSection[];
  sectionGroups?: ReportDocumentSectionGroup[];
  title: string;
}

export interface ReportDocumentSource {
  filename: string;
  html: string;
}

export interface ReportDocumentSectionOption<TSectionId extends string = string> {
  description: string;
  id: TSectionId;
  title: string;
}

export interface ReportDocumentPreferences<TSectionId extends string = string> {
  includeContext: boolean;
  selectedSectionIds: TSectionId[];
}

export type GeneratedReportDocument =
  | {
      downloadUrl: string;
      filename: string;
      kind: 'pdf';
      openUrl: string;
      refreshAfterMs?: number;
    }
  | {
      filename: string;
      html: string;
      kind: 'print';
    };
